import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { apiService } from '../lib/api';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // If supabase is not configured, skip auth setup
        if (!supabase) {
            console.warn('Supabase not configured, auth disabled');
            setLoading(false);
            return;
        }

        // Check active sessions
        const checkSession = async () => {
            try {
                const { data: { session } } = await supabase.auth.getSession();
                if (session?.user) {
                    await handleAuthUser(session.user);
                }
            } catch (error) {
                console.error('Error checking session:', error);
            }
            setLoading(false);
        };

        checkSession();

        // Listen for auth changes
        let subscription = null;
        try {
            const { data } = supabase.auth.onAuthStateChange(async (event, session) => {
                if (event === 'SIGNED_IN' && session?.user) {
                    await handleAuthUser(session.user);
                } else if (event === 'SIGNED_OUT') {
                    setUser(null);
                    localStorage.removeItem('supabase_user_id');
                }
            });
            subscription = data?.subscription;
        } catch (error) {
            console.error('Error setting up auth listener:', error);
        }

        return () => {
            if (subscription) {
                subscription.unsubscribe();
            }
        };
    }, []);

    const handleAuthUser = async (supabaseUser) => {
        try {
            // Store supabase ID for API auth
            localStorage.setItem('supabase_user_id', supabaseUser.id);
            
            // Create or get user from our backend
            const { data } = await apiService.createUser({
                email: supabaseUser.email,
                supabase_id: supabaseUser.id,
            });
            
            setUser({
                ...data,
                email: supabaseUser.email,
            });
        } catch (error) {
            console.error('Error handling auth user:', error);
        }
    };

    const signUp = async (email, password) => {
        if (!supabase) {
            return { data: null, error: { message: 'Auth not configured' } };
        }
        try {
            // Use XMLHttpRequest to avoid fetch interception issues
            const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
            const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
            
            const data = await new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                xhr.open('POST', `${supabaseUrl}/auth/v1/signup`, true);
                xhr.setRequestHeader('Content-Type', 'application/json');
                xhr.setRequestHeader('apikey', supabaseKey);
                
                xhr.onload = function() {
                    try {
                        const response = JSON.parse(xhr.responseText);
                        if (xhr.status >= 200 && xhr.status < 300) {
                            resolve({ ok: true, data: response });
                        } else {
                            resolve({ ok: false, data: response });
                        }
                    } catch (e) {
                        reject(new Error('Failed to parse response'));
                    }
                };
                
                xhr.onerror = function() {
                    reject(new Error('Network error'));
                };
                
                xhr.send(JSON.stringify({ email, password }));
            });
            
            if (!data.ok) {
                return { data: null, error: { message: data.data.msg || data.data.error_description || 'Signup failed' } };
            }
            
            return { data: data.data, error: null };
        } catch (err) {
            console.error('Signup error:', err);
            return { data: null, error: { message: err.message || 'An unexpected error occurred' } };
        }
    };

    const signIn = async (email, password) => {
        if (!supabase) {
            return { data: null, error: { message: 'Auth not configured' } };
        }
        try {
            // Use XMLHttpRequest to avoid fetch interception issues
            const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
            const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
            
            const data = await new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                xhr.open('POST', `${supabaseUrl}/auth/v1/token?grant_type=password`, true);
                xhr.setRequestHeader('Content-Type', 'application/json');
                xhr.setRequestHeader('apikey', supabaseKey);
                
                xhr.onload = function() {
                    try {
                        const response = JSON.parse(xhr.responseText);
                        if (xhr.status >= 200 && xhr.status < 300) {
                            resolve({ ok: true, data: response });
                        } else {
                            resolve({ ok: false, data: response });
                        }
                    } catch (e) {
                        reject(new Error('Failed to parse response'));
                    }
                };
                
                xhr.onerror = function() {
                    reject(new Error('Network error'));
                };
                
                xhr.send(JSON.stringify({ email, password }));
            });
            
            if (!data.ok) {
                return { data: null, error: { message: data.data.msg || data.data.error_description || 'Invalid login credentials' } };
            }
            
            // Set session manually
            if (data.data.access_token) {
                await supabase.auth.setSession({
                    access_token: data.data.access_token,
                    refresh_token: data.data.refresh_token,
                });
            }
            
            return { data: data.data, error: null };
        } catch (err) {
            console.error('SignIn error:', err);
            return { data: null, error: { message: err.message || 'An unexpected error occurred' } };
        }
    };

    const signOut = async () => {
        if (supabase) {
            await supabase.auth.signOut();
        }
        setUser(null);
        localStorage.removeItem('supabase_user_id');
    };

    const value = {
        user,
        loading,
        signUp,
        signIn,
        signOut,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
